import React, { useEffect, useRef } from 'react';
import { useNavigation } from './LanguageContext';

interface AnalyticsProps {
    gaMeasurementId?: string;
    clarityProjectId?: string;
}

// Global declarations for Google's gtag
declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const Analytics: React.FC<AnalyticsProps> = ({ gaMeasurementId }) => {
    const { path } = useNavigation();
    const isInitialized = useRef<boolean>(false);
    const lastTrackedPath = useRef<string>('');

    useEffect(() => {
        if (!gaMeasurementId || typeof window === 'undefined') return;
        if (!['buildhub.global', 'www.buildhub.global'].includes(window.location.hostname)) return;
        if (window.location.pathname.startsWith('/console')) return;

        const internalStorageKey = 'buildhub_internal_traffic';
        const currentUrl = new URL(window.location.href);
        const internalParam = currentUrl.searchParams.get('bh_internal');

        try {
            if (internalParam === '1') localStorage.setItem(internalStorageKey, '1');
            if (internalParam === '0') localStorage.removeItem(internalStorageKey);
        } catch {
            // Tracking still works when browser storage is unavailable.
        }

        if (internalParam !== null) {
            currentUrl.searchParams.delete('bh_internal');
            window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
        }

        let isInternalTraffic = false;
        try {
            isInternalTraffic = localStorage.getItem(internalStorageKey) === '1';
        } catch {
            isInternalTraffic = false;
        }

        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        if (isInternalTraffic) window.gtag('set', { traffic_type: 'internal' });

        const initialPath = `${window.location.pathname}${window.location.search}`;
        window.gtag('config', gaMeasurementId, {
            page_path: initialPath,
            send_page_view: true,
            ...(isInternalTraffic ? { traffic_type: 'internal' } : {})
        });
        lastTrackedPath.current = initialPath;
        isInitialized.current = true;

        const scriptId = `buildhub-ga-${gaMeasurementId}`;
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId)}`;
            document.head.appendChild(script);
        }

        const normHref = (anchor: HTMLAnchorElement) => (anchor.getAttribute('href') || '').trim();
        const isSocial = (url: string) => /linkedin\.com|instagram\.com|facebook\.com|tiktok\.com|x\.com|twitter\.com/i.test(url);
        const isPartner = (url: string) => /arhideko\.rs|kate-interiors\.ae|buildhub\.rs/i.test(url);

        const handleClick = (event: MouseEvent) => {
            if (!window.gtag) return;

            const target = event.target as HTMLElement;
            const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
            if (!anchor) return;

            const href = normHref(anchor);
            if (!href) return;

            const absoluteUrl = anchor.href || href;

            if (/^mailto:/i.test(href)) {
                window.gtag('event', 'lead_email', { contact_method: 'email' });
                return;
            }
            if (/wa\.me|whatsapp/i.test(absoluteUrl)) {
                const region = anchor.dataset.contactRegion;
                window.gtag('event', 'lead_whatsapp', {
                    contact_method: 'whatsapp',
                    ...(region ? { region } : {}),
                });
                return;
            }

            const isExternal = /^https?:\/\//i.test(absoluteUrl)
                && new URL(absoluteUrl).hostname !== window.location.hostname;
            if (isExternal) {
                const destinationHost = new URL(absoluteUrl).hostname.replace(/^www\./, '');
                if (isSocial(absoluteUrl)) {
                    window.gtag('event', 'outbound_social', { destination_host: destinationHost });
                } else if (isPartner(absoluteUrl)) {
                    window.gtag('event', 'outbound_partner', { destination_host: destinationHost });
                } else {
                    window.gtag('event', 'outbound_click', { destination_host: destinationHost });
                }
            }
        };

        document.addEventListener('click', handleClick, { passive: true });

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [gaMeasurementId]);

    // Handle React router (SPA) page navigations
    useEffect(() => {
        if (!isInitialized.current || typeof window === 'undefined' || !window.gtag || !gaMeasurementId) return;
        if (window.location.pathname.startsWith('/console')) return;

        const pagePath = `${window.location.pathname}${window.location.search}`;
        if (pagePath === lastTrackedPath.current) return;

        window.gtag('event', 'page_view', { page_path: pagePath });
        lastTrackedPath.current = pagePath;
    }, [path, gaMeasurementId]);

    return null;
};

export default Analytics;
